import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];

const getId = (x) => x?.AccountId ?? x?.accountId ?? x?.Id ?? x?.id ?? "";
const getCode = (x) => x?.AccountCode ?? x?.accountCode ?? "";
const getName = (x) =>
  x?.AccountName ?? x?.accountName ?? x?.Name ?? x?.name ?? "";

const styles = {
  page: { padding: 0, fontSize: 11 },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { fontSize: 13, fontWeight: 700 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 3,
    color: "#495057",
  },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: {
    height: 30,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
    padding: "3px 12px",
  },
  info: {
    marginTop: 10,
    fontSize: 11,
    padding: "8px 10px",
    borderRadius: 10,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
  },
};

export default function ContraVoucherPage() {
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    voucherNo: "",
    voucherDate: today(),
    fromAccountId: "",
    toAccountId: "",
    amount: "",
    notes: "",
  });

  useEffect(() => {
    loadAccounts();
    setForm((p) => ({ ...p, voucherNo: generateVoucherNo() }));
  }, []);

  const bankCashAccounts = useMemo(() => {
    return accounts.filter((a) => {
      const n = getName(a).toLowerCase();
      return n.includes("cash") || n.includes("bank");
    });
  }, [accounts]);

  const generateVoucherNo = () => {
    const d = new Date();

    return `CNTR-${d.getFullYear()}${String(d.getMonth() + 1).padStart(
      2,
      "0",
    )}${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(
      2,
      "0",
    )}${String(d.getMinutes()).padStart(2, "0")}${String(
      d.getSeconds(),
    ).padStart(2, "0")}`;
  };

  const loadAccounts = async () => {
    const res = await client.get("/accounts");
    setAccounts(Array.isArray(res.data) ? res.data : []);
  };

  const clearForm = () => {
    setForm({
      voucherNo: generateVoucherNo(),
      voucherDate: today(),
      fromAccountId: "",
      toAccountId: "",
      amount: "",
      notes: "",
    });
  };

  const save = async () => {
    if (!form.voucherNo.trim()) return alert("Voucher No required");
    if (!form.fromAccountId) return alert("Select From Account");
    if (!form.toAccountId) return alert("Select To Account");

    if (String(form.fromAccountId) === String(form.toAccountId)) {
      return alert("From and To account cannot be same");
    }

    if (!form.amount || Number(form.amount) <= 0) {
      return alert("Enter valid amount");
    }

    try {
      setSaving(true);

      await client.post("/vouchers/contra", {
        companyId: Number(localStorage.getItem("companyId") || 0),
        voucherNo: form.voucherNo.trim(),
        voucherDate: form.voucherDate,
        fromAccountId: Number(form.fromAccountId),
        toAccountId: Number(form.toAccountId),
        amount: Number(form.amount),
        notes: form.notes,
      });

      alert("Contra voucher saved and journal posted");
      clearForm();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to save contra voucher");
    } finally {
      setSaving(false);
    }
  };

  const fromAccount = bankCashAccounts.find(
    (a) => String(getId(a)) === String(form.fromAccountId),
  );

  const toAccount = bankCashAccounts.find(
    (a) => String(getId(a)) === String(form.toAccountId),
  );

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>
              <i className="bi bi-arrow-left-right me-2"></i>
              Contra Voucher
            </div>
            <div style={styles.sub}>Cash ↔ Bank transfer with auto journal</div>
          </div>

          <span
            className="badge bg-light text-primary"
            style={{ fontSize: 10 }}
          >
            Auto Journal
          </span>
        </div>

        <div style={styles.body}>
          <div className="row g-2">
            <Field label="Voucher No" className="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.voucherNo}
                onChange={(e) =>
                  setForm({ ...form, voucherNo: e.target.value })
                }
              />
            </Field>

            <Field label="Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.voucherDate}
                onChange={(e) =>
                  setForm({ ...form, voucherDate: e.target.value })
                }
              />
            </Field>

            <Field label="From Account" className="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.fromAccountId}
                onChange={(e) =>
                  setForm({ ...form, fromAccountId: e.target.value })
                }
              >
                <option value="">Select Cash / Bank</option>
                {bankCashAccounts.map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getCode(a) ? `${getCode(a)} - ${getName(a)}` : getName(a)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="To Account" className="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.toAccountId}
                onChange={(e) =>
                  setForm({ ...form, toAccountId: e.target.value })
                }
              >
                <option value="">Select Cash / Bank</option>
                {bankCashAccounts.map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getCode(a) ? `${getCode(a)} - ${getName(a)}` : getName(a)}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Amount" className="col-md-4">
              <input
                type="number"
                className="form-control form-control-sm text-end"
                style={styles.input}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Field>

            <Field label="Notes" className="col-md-8">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Example: Enter purpose, reference bill/invoice no., payment terms or adjustment reason"
              />
            </Field>
          </div>

          <div style={styles.info}>
            <strong>From:</strong> {fromAccount ? getName(fromAccount) : "-"} |{" "}
            <strong>To:</strong> {toAccount ? getName(toAccount) : "-"} |{" "}
            <strong>Amount:</strong> ₹ {Number(form.amount || 0).toFixed(2)}
          </div>

          <div className="d-flex gap-2 mt-2">
            <button
              onClick={save}
              disabled={saving}
              className="btn btn-primary btn-sm"
              style={styles.btn}
            >
              <i className="bi bi-check2-circle me-1"></i>
              {saving ? "Saving..." : "Save"}
            </button>

            <button
              onClick={clearForm}
              className="btn btn-outline-secondary btn-sm"
              style={styles.btn}
            >
              <i className="bi bi-x-circle me-1"></i>
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}
